/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Rendra Pramulia
*/

import React, { useEffect, useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';

import * as Layout from '@components/screen/Layout';
import {
  SociollaInput,
  SociollaSelectBox,
  SociollaSalesTeamSearch,
  SociollaMarketTypeSearch,
  SociollaCustomerClusterSearch,
  SociollaPaymentTermsSearch,
} from '@components/sociolla/form';
import Input from '@components/form/Input';
import SociollaText from '@components/sociolla/SociollaText';

import Colors from '@global/colors';
import useApi from '@/hooks/useApi';
import { isWeb } from '@global/screens';
import { MsB2b } from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import { hasNoAccess } from '@global/helpers';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';

import PropTypes from 'prop-types';

const propTypes = {
  data: PropTypes.object,
};

const defaultProps = {
  data: {},
};

function CustomerSales(props) {
  const auth = useSelector((state) => state.auth);
  const role = useSelector((state) => state.auth.user?.role?.code || '');
  const isNewCustomer = Boolean(
    !props?.data?.status || props?.data?.status == 'draft',
  );
  const isAdmin = Boolean(
    ['superadmin', 'sales_admin', 'general'].indexOf(role) > -1,
  );

  const {
    setFieldValue,
    setFieldTouched,
    values,
    errors,
    touched,
    submitCount,
  } = useFormikContext();

  const [bussinessType, setBussinessType] = useState([]);
  const [account, setAccount] = useState([]);
  const getCustomerTypeApi = useApi(MsAccount.getCustomerTypes);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [salesIds, setSalesIds] = useState([]);

  useEffect(() => {
    async function fetchCustomerTypes() {
      try {
        const res = await getCustomerTypeApi.request({
          params: {
            filter: {
              is_active: true,
            },
            fields: 'name,description,_id',
          },
        });
        if (res?.status === 200 && res.data?.data?.length) {
          const types = res.data.data.map((item) => ({
            value: {
              _id: item._id,
              name: item.name,
              description: item.description,
            },
            label: item.name,
          }));
          setCustomerTypes(types);
        } else {
          setCustomerTypes([]);
        }
      } catch (e) {
        console.error('Failed to fetch customer types', e);
        setCustomerTypes([]);
      }
    }

    fetchCustomerTypes();
  }, []);

  // const [showCluster, setShowCluster] = useState(false);
  const salesTeamFilter = useMemo(() => {
    const filter = Boolean(
      ['salesman', 'sales_supervisor', 'sales_admin'].indexOf(role) > -1,
    );
    if (filter) {
      return {
        _id: { $in: auth.user.sales_team.map((x) => x.id) },
      };
    } else {
      return {};
    }
  }, [role]);
  // const marketTypeFilter = useMemo(() => {
  //   if (values.sales_team.market_type) {
  //     return {
  //       _id: values.sales_team.market_type.id,
  //     };
  //   } else {
  //     return {};
  //   }
  // }, [values.sales_team]);

  const editableFields = !hasNoAccess([
    // these roles can edit limit amount & payment terms field
    'sales_admin',
    'superadmin',
    'sales_supervisor',
    'salesman',
    'general',
  ]);
  const onChangeMarketTypes = (val) => {
    setFieldValue('bussiness_type', '');
    setFieldValue('account', '');
    setFieldValue('key_account_manager', '');
    setFieldValue('account_manager', '');
    for (let i = 0; i < val?.business_type?.length; i++) {
      if (val.business_type[i].description) {
        val.business_type[
          i
        ].label = `${val.business_type[i].code} (${val.business_type[i].description})`;
      }
    }
    const updatedBt = val.business_type.filter((bt) => bt.is_active);
    setBussinessType(updatedBt);
  };

  const onChangeBusinessTypes = (val) => {
    const modifiedVal = {
      cerebro_id: val.cerebro_id,
      code: val.code,
      _id: val._id || val?.id,
    };
    setFieldValue('bussiness_type', modifiedVal);
    setFieldValue('market_type', {
      ...values.market_type,
      business_type: modifiedVal,
    });
    setFieldValue('account', '');
    if (isNewCustomer && val?.customer) {
      if (val.customer?.payment_terms?.id) {
        setFieldValue('payment_terms', val.customer.payment_terms);
      }
      if (val.customer?.cluster?.id) {
        setFieldValue('cluster', val.customer.cluster);
      }
      if (val.customer?.limit_amount || val.customer?.limit_amount == 0) {
        setFieldValue('limit_amount', val.customer.limit_amount.toString());
      }
    }
    getAccount(val._id || val?.id);
  };

  const onChangeAccount = (val) => {
    const modifiedVal = {
      cerebro_id: val.cerebro_id,
      code: val.code,
      id: val.id,
    };
    setFieldValue('account', modifiedVal);
  };

  const fetchSalesTeamPersonsByRole = async (
    salesTeamId,
    roleCode,
    fieldName,
    { onlyIfSingle = false } = {},
  ) => {
    try {
      const res = await MsAccount.getUsers({
        params: {
          filter: {
            is_active: true,
            'role.code': roleCode,
            'sales_team.id': salesTeamId,
          },
          limit: 100,
        },
      });
      if (res?.status === 200 && res.data?.data?.length) {
        const users = res.data.data.map((u) => ({ id: u._id, name: u.name }));
        // [B2B-5933] autofill only when the sales team has a single person, else user picks
        setFieldValue(fieldName, onlyIfSingle && users.length > 1 ? [] : users);
      } else {
        setFieldValue(fieldName, []);
      }
    } catch (e) {
      setFieldValue(fieldName, []);
    }
  };

  const onChangeSalesTeam = (val) => {
    const salesman = [];
    const beautyAdvisors = [];
    const merchandisers = [];
    val.team_members.map((user) => {
      if (user?.role?.code == 'salesman') {
        salesman.push(user);
      } else if (user?.role?.code == 'beauty_advisor') {
        beautyAdvisors.push(user);
      } else if (user?.role?.code == 'merchandiser') {
        merchandisers.push(user);
      }
    });
    getSalesPerson(salesman);
    setFieldValue('user', '');
    setFieldTouched('user', false);
    setFieldValue('market_type', '');
    setFieldValue('bussiness_type', '');
    setFieldValue('account', '');
    setFieldValue('key_account_manager', '');
    setFieldValue('account_manager', '');
    setFieldValue('sales_admin', []);
    setFieldValue('regional_distributor_manager', []);
    if (val?.id) {
      fetchSalesTeamPersonsByRole(val.id, 'sales_admin', 'sales_admin', {
        onlyIfSingle: true,
      });
      fetchSalesTeamPersonsByRole(
        val.id,
        'regional_distributor_manager',
        'regional_distributor_manager',
      );
    }
  };

  // const getClusterCustomer = async () => {
  //   try {
  //     if (
  //       props.data?.status &&
  //       props.data?.status === 'approved' &&
  //       props.data?._id &&
  //       isAdmin
  //     ) {
  //       setShowCluster(true);
  //     } else if (isNewCustomer) {
  //       setShowCluster(true);
  //     }
  //   } catch (e) {
  //     setShowCluster(false);
  //   }
  // };

  const getSalesTeam = (id) => {
    MsB2b.getSalesTeam({
      params: {
        filter: {
          _id: id,
        },
      },
    }).then((res) => {
      if (res.status === 200 && res.data?.data?.length) {
        const salesman = [];
        const beautyAdvisors = [];
        const merchandisers = [];
        res.data.data[0].team_members.map((user) => {
          if (user?.role?.code == 'salesman') {
            salesman.push(user);
          } else if (user?.role?.code == 'beauty_advisor') {
            beautyAdvisors.push(user);
          } else if (user?.role?.code == 'merchandiser') {
            merchandisers.push(user);
          }
        });
        getSalesPerson(salesman);
      }
    });
  };

  const getSalesPerson = (sales = []) => {
    const ids = sales.map((a) => a.id);
    if (ids?.length) {
      setSalesIds(ids);
    }
  };

  const getMarketType = (id) => {
    MsB2b.getMarketType({
      params: {
        filter: {
          _id: id,
        },
      },
    }).then((res) => {
      if (res.status === 200 && res.data?.data?.length) {
        const businessTypeData = res.data.data[0].business_type
          .map((v) => ({
            ...v,
            label: v.description ? `${v.code} (${v.description})` : v.code,
          }))
          .filter((v) => v.is_active);
        setBussinessType(businessTypeData);

        if (values?.market_type?.business_type?.id) {
          getAccount(values?.market_type?.business_type?.id);
        }
      }
    });
  };

  const getAccount = (id) => {
    MsB2b.getMarketType({
      params: {
        filter: {
          is_account: true,
          'parent_group.id': id,
          is_active: true,
        },
        limit: 50,
      },
    }).then((res) => {
      if (res.status === 200) {
        const result = res.data.data.map((item) => ({
          id: item._id,
          code: item.code,
          cerebro_id: item.cerebro_id,
        }));
        setAccount(result);
        setFieldValue('market_type', {
          ...values.market_type,
          business_type: {
            ...values.business_type,
            account: result,
          },
        });
      }
    });
  };

  // useEffect(() => {
  //   // setShowCluster(false);
  //   getClusterCustomer();
  // }, []);

  useEffect(() => {
    if (props.data.sales_team?.id && salesIds.length === 0) {
      getSalesTeam(props.data.sales_team.id);
    }
    if (props.data.market_type?.id && bussinessType.length === 0) {
      getMarketType(props.data.market_type.id);
    }
    if (props.data?.bussiness_type?.id) {
      onChangeBusinessTypes(props.data?.bussiness_type);
    }
    if (props.data?.account?.id) {
      onChangeAccount(props.data.account);
    }
    if (props.data.sales_team?.id) {
      // [B2B-5933] keep the saved Sales Admin as is; an empty one stays empty for the user to pick
      if (props.data.sales_admin?.length) {
        setFieldValue('sales_admin', props.data.sales_admin);
      }
      if (props.data.regional_distributor_manager?.length) {
        setFieldValue(
          'regional_distributor_manager',
          props.data.regional_distributor_manager,
        );
      } else {
        fetchSalesTeamPersonsByRole(
          props.data.sales_team.id,
          'regional_distributor_manager',
          'regional_distributor_manager',
        );
      }
    }
  }, [props.data]);

  const renderSalesTeamSearch = () => (
    <Layout.FormGroup>
      <SociollaSalesTeamSearch
        testID="customer-sales-sales-team-select"
        isTransparent
        customFilter={salesTeamFilter}
        name="sales_team"
        onChange={onChangeSalesTeam}
        isRequired
      />
    </Layout.FormGroup>
  );

  const renderMarketTypeSearch = () => (
    <Layout.FormGroup>
      <SociollaMarketTypeSearch
        testID="customer-sales-market-type-select"
        isTransparent
        name="market_type"
        onChange={onChangeMarketTypes}
        editable={!!values['sales_team']}
        disabled={!values['sales_team']}
        isRequired
      />
    </Layout.FormGroup>
  );

  const renderSelectBox = (
    label,
    options,
    name,
    trackBy,
    labelKey,
    onChange,
    dependency,
  ) => (
    <Layout.FormGroup>
      <SociollaSelectBox
        testID={`customer-sales-${name}-select`}
        isTransparent
        label={label}
        options={options}
        name={name}
        trackBy={trackBy}
        labelKey={labelKey}
        onChange={onChange}
        editable={!!values[dependency]}
        disabled={!values[dependency]}
        isRequired
      />
    </Layout.FormGroup>
  );

  const renderReadOnlyPersonField = (label, fieldName) => {
    const persons = values[fieldName];
    const displayValue =
      Array.isArray(persons) && persons.length
        ? persons.map((p) => p.name).join(', ')
        : '';
    return (
      <Layout.FormGroup>
        <Input
          testID={`customer-sales-${fieldName}-input`}
          label={label}
          value={displayValue}
          editable={false}
          isTransparent
        />
      </Layout.FormGroup>
    );
  };

  const salesFormComponent = () => (
    <>
      {renderSalesTeamSearch()}
      {renderMarketTypeSearch()}
      <Layout.FormGroup>
        <SociollaSelectBox
          testID="customer-sales-business-type-select"
          label="Business Type"
          options={bussinessType}
          name="bussiness_type"
          trackBy="_id"
          labelKey="label"
          formLabelKey="code"
          onChange={onChangeBusinessTypes}
          editable={values['market_type'] ? true : false}
          disabled={values['market_type'] ? false : true}
          isRequired
          isTransparent
        />
      </Layout.FormGroup>
      {renderSelectBox(
        'Account',
        account,
        'account',
        'id',
        'code',
        onChangeAccount,
        'bussiness_type',
      )}
      {renderReadOnlyPersonField(
        'Regional Distributor Manager',
        'regional_distributor_manager',
      )}
      {renderSalesPersonSearch({
        label: 'Sales Admin',
        name: 'sales_admin',
        customFilter: {
          is_active: true,
          'role.code': 'sales_admin',
          'sales_team.id': values['sales_team']?.id,
        },
        customSort: { name: 1 },
        isRequired: true,
      })}
    </>
  );

  const renderSalesPersonSearch = ({
    label,
    name,
    isMultiple = true,
    labelKey = 'name',
    customFilter = {},
    useCallbackOptions = [],
    isRequired = false,
    keepLoadMore = false,
    notFetch,
    editable = true,
    customSort = { sales_code: 1 },
  }) => {
    const fieldError = errors?.[name];
    const shouldShowError = fieldError && (touched?.[name] || submitCount > 0);
    const displayedError = shouldShowError ? fieldError : false;
    const isNotFetch =
      notFetch !== undefined ? notFetch : !values['sales_team']?.id;

    return (
      <Layout.FormGroup>
        <SalesPersonSearch
          testID={`customer-sales-${label
            .toLowerCase()
            .replace(/\s+/g, '-')}-select`}
          isTransparent
          labelKey={labelKey}
          label={label}
          placeholder={`Select ${label}`}
          value={values[name]}
          multiple={isMultiple}
          notFetch={isNotFetch}
          editable={editable}
          isRequired={isRequired}
          hasError={displayedError}
          customFilter={{
            remove_default_filter: true,
            ...customFilter,
          }}
          onChange={async (value) => {
            await setFieldValue(name, value);
            setFieldTouched(name, true, false);
          }}
          useCallbackOptions={useCallbackOptions}
          keepLoadMore={keepLoadMore}
          customSort={customSort}
        />
      </Layout.FormGroup>
    );
  };

  const getKeyAccountManagerFilter = () => {
    const marketType = values.market_type?.code?.toUpperCase();
    const salesTeamId = values['sales_team']?.id;
    const filter = {
      'sales_team.id': salesTeamId,
    };
    if (marketType === 'GT') {
      return { ...filter, 'role.code': 'regional_manager' };
    }
    if (marketType === 'MT') {
      return { ...filter, 'role.code': 'group_key_account_manager' };
    }

    return {
      'role.code': { $in: ['regional_manager', 'group_key_account_manager'] },
    };
  };

  const getAccountManagerFilter = () => {
    const marketType = values.market_type?.code?.toUpperCase();
    const salesTeamId = values['sales_team']?.id;
    const filter = {
      'sales_team.id': salesTeamId,
    };
    if (marketType === 'GT') {
      return { ...filter, 'role.code': 'regional_support' };
    }
    if (marketType === 'MT') {
      return { ...filter, 'role.code': 'key_account_manager' };
    }

    return {
      'role.code': { $in: ['regional_support', 'key_account_manager'] },
    };
  };

  const salesUserFormComponent = () => {
    const isKamAmEditable =
      values['sales_team']?.id && values['market_type']?.id;
    return (
      <>
        {renderSalesPersonSearch({
          label: 'Sales Person',
          name: 'user',
          isMultiple: false,
          labelKey: 'sales_code',
          isRequired: true,
          customFilter: {
            is_active: true,
            _id: { $in: salesIds },
          },
          useCallbackOptions: [salesIds],
        })}
        {renderSalesPersonSearch({
          label: 'Beauty Advisor',
          name: 'beauty_advisor',
          customFilter: {
            'role.code': 'beauty_advisor',
            'sales_team.id': values['sales_team']?.id,
          },
        })}
        {renderSalesPersonSearch({
          label: 'Merchandiser',
          name: 'merchandiser',
          customFilter: {
            'role.code': 'merchandiser',
            'sales_team.id': values['sales_team']?.id,
          },
        })}
        <>
          {renderSalesPersonSearch({
            label: 'Key Account Manager',
            name: 'key_account_manager',
            customFilter: getKeyAccountManagerFilter(),
            keepLoadMore: true,
            notFetch: !isKamAmEditable,
            editable: !!isKamAmEditable,
          })}
          {renderSalesPersonSearch({
            label: 'Account Manager',
            name: 'account_manager',
            customFilter: getAccountManagerFilter(),
            keepLoadMore: true,
            notFetch: !isKamAmEditable,
            editable: !!isKamAmEditable,
          })}
        </>
      </>
    );
  };

  const isRequired = (values) =>
    ['GT', 'gt'].includes(values['market_type'].code);

  const renderSociollaInput = (name, label, values, extraProps = {}) => (
    <Layout.FormGroup>
      <SociollaInput
        testID={`customer-sales-${name}-input`}
        isTransparent={true}
        name={name}
        label={label}
        isRequired={isRequired(values)}
        {...extraProps}
      />
    </Layout.FormGroup>
  );

  const renderBankInputs = () => (
    <>
      {renderSociollaInput('bank_name', 'Bank Name', values, {
        isUppercase: true,
      })}
      {renderSociollaInput('bank_branch', 'Branch', values, {
        isUppercase: true,
      })}
      {renderSociollaInput('bank_account_number', 'Account Number', values, {
        keyboardType: 'numeric',
        isNumeric: true,
      })}
      {renderSociollaInput('bank_account_name', 'Account Name', values, {
        isUppercase: true,
      })}
    </>
  );

  return (
    <View testID="customer-sales-container">
      <SociollaText
        type="medium"
        style={{ fontSize: 16, color: Colors.black2, marginBottom: 30 }}>
        Sales Info
      </SociollaText>
      <View style={[styles.row, isWeb ? {} : { flexDirection: 'column' }]}>
        <View style={isWeb ? { flex: 1 } : {}}>
          {salesFormComponent()}
          {salesUserFormComponent()}
        </View>
        {isWeb && <View style={styles.separator} />}
        <View style={isWeb ? { flex: 1 } : {}}>
          {renderBankInputs()}
          <Layout.FormGroup>
            <View style={{ flexDirection: 'row', width: '100%' }}>
              <View style={{ flex: 1, marginRight: 10 }}>
                <SociollaInput
                  testID="customer-sales-special-expired-period-input"
                  keyboardType="numeric"
                  name="special_expired_period"
                  label="Special Expirate Date"
                  isNumeric
                  isRequired
                />
              </View>
              <View
                style={{
                  flex: 1,
                  marginTop: 38,
                }}>
                <SociollaText style={{ fontSize: 12 }}>months</SociollaText>
              </View>
            </View>
          </Layout.FormGroup>
          <Layout.FormGroup>
            <SociollaPaymentTermsSearch
              testID="customer-sales-payment-terms-select"
              isTransparent={true}
              name="payment_terms"
              isRequired
              // editable={!isNewCustomer}
              editable={isNewCustomer || editableFields}
              disabled={isNewCustomer || editableFields}
            />
          </Layout.FormGroup>
          <Layout.FormGroup>
            <SociollaInput
              testID="customer-sales-limit-amount-input"
              isTransparent={true}
              label="Limit Amount"
              name="limit_amount"
              isNumeric
              maxLength={13}
              // editable={!isNewCustomer}
              editable={isNewCustomer || editableFields}
              disabled={isNewCustomer || editableFields}
              isRequired
              keyboardType="numeric"
            />
          </Layout.FormGroup>
          <Layout.FormGroup>
            <SociollaCustomerClusterSearch
              testID="customer-sales-cluster-select"
              isTransparent={true}
              name="cluster"
              isRequired
              editable={isAdmin}
              disabled={isNewCustomer}
              customFilter={{ is_active: true }}
            />
          </Layout.FormGroup>
          <Layout.FormGroup></Layout.FormGroup>
          <Layout.FormGroup>
            <SociollaInput
              testID="customer-sales-special-discount-input"
              isTransparent={true}
              // keyboardType="numeric"
              name="special_discount"
              label="Special Discount"
              maxLength={13}
              // isNumeric
            />
          </Layout.FormGroup>
          <Layout.FormGroup>
            <SociollaSelectBox
              testID="customer-sales-customer-type-select"
              isTransparent={true}
              label="Customer Type"
              options={customerTypes}
              name="customer_type"
              trackBy="value._id"
              labelKey="label"
              isRequired
              withReset={true}
            />
          </Layout.FormGroup>
        </View>
      </View>
    </View>
  );
}

CustomerSales.propTypes = propTypes;
CustomerSales.defaultProps = defaultProps;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    width: '100%',
  },
  separator: {
    height: '100%',
    width: 1,
    backgroundColor: Colors.grey9,
    marginHorizontal: 30,
  },
});

export default CustomerSales;
